import { useState } from "react";
import { motion } from "framer-motion";
import { Mail, MapPin, Globe, Send } from "lucide-react";
import { toast } from "sonner";
import SiteHeader from "@/components/shop/SiteHeader";
import SiteFooter from "@/components/shop/SiteFooter";
import CartDrawer from "@/components/shop/CartDrawer";
import { api } from "@/lib/api";

export default function Contact() {
  const [form, setForm] = useState({ name: "", email: "", subject: "", message: "" });
  const [loading, setLoading] = useState(false);

  const submit = async (e) => {
    e.preventDefault();
    if (!form.name || !form.email || !form.message) {
      toast.error("Please fill in your name, email, and message.");
      return;
    }
    setLoading(true);
    try {
      await api.post("/contact", form);
      toast.success("Thanks! We'll get back to you soon.");
      setForm({ name: "", email: "", subject: "", message: "" });
    } catch {
      toast.error("Something went wrong. Please email us directly.");
    } finally {
      setLoading(false);
    }
  };

  const set = (k) => (e) => setForm((f) => ({ ...f, [k]: e.target.value }));

  return (
    <div className="min-h-screen bg-white text-slate-900" data-testid="contact-page">
      <SiteHeader />
      <CartDrawer />

      <section className="border-b border-slate-100 bg-gradient-to-b from-sky-50/60 to-white">
        <div className="mx-auto max-w-5xl px-5 sm:px-8 py-20 md:py-24">
          <motion.p initial={{ opacity: 0 }} animate={{ opacity: 1 }} className="text-xs uppercase tracking-[0.25em] text-amber-500 font-bold">
            Say hello
          </motion.p>
          <motion.h1
            initial={{ opacity: 0, y: 16 }} animate={{ opacity: 1, y: 0 }}
            className="mt-3 font-display text-5xl md:text-6xl font-black text-[#0f2a5c] leading-tight"
          >
            We&apos;d love to<br />hear from you.
          </motion.h1>
          <p className="mt-5 max-w-xl text-slate-600 text-lg">
            Wholesale enquiries, retail partnerships, press or a simple hello — send us a note.
          </p>
        </div>
      </section>

      <section className="mx-auto max-w-6xl px-5 sm:px-8 py-16 grid grid-cols-1 lg:grid-cols-5 gap-12">
        {/* Info */}
        <div className="lg:col-span-2 space-y-6">
          <div className="rounded-2xl border border-slate-100 p-5" data-testid="contact-info-email">
            <div className="flex items-center gap-3">
              <div className="w-10 h-10 rounded-full bg-sky-50 flex items-center justify-center text-[#173a7a]">
                <Mail size={18} />
              </div>
              <div>
                <p className="text-xs uppercase tracking-widest font-bold text-slate-500">Email</p>
                <a href="mailto:sales@morningsofttissue.com" className="font-semibold text-slate-900">sales@morningsofttissue.com</a>
              </div>
            </div>
          </div>
          <div className="rounded-2xl border border-slate-100 p-5" data-testid="contact-info-website">
            <div className="flex items-center gap-3">
              <div className="w-10 h-10 rounded-full bg-sky-50 flex items-center justify-center text-[#173a7a]">
                <Globe size={18} />
              </div>
              <div>
                <p className="text-xs uppercase tracking-widest font-bold text-slate-500">Website</p>
                <p className="font-semibold text-slate-900">www.morningsofttissue.com</p>
              </div>
            </div>
          </div>
          <div className="rounded-2xl border border-slate-100 p-5" data-testid="contact-info-address">
            <div className="flex items-start gap-3">
              <div className="w-10 h-10 rounded-full bg-sky-50 flex items-center justify-center text-[#173a7a]">
                <MapPin size={18} />
              </div>
              <div>
                <p className="text-xs uppercase tracking-widest font-bold text-slate-500">Head Office</p>
                <p className="font-semibold text-slate-900">Morning Soft LLC</p>
                <p className="text-sm text-slate-600">Angleton, Texas 77515</p>
                <p className="text-sm text-slate-600">United States</p>
              </div>
            </div>
          </div>
        </div>

        {/* Form */}
        <form onSubmit={submit} noValidate className="lg:col-span-3 rounded-3xl border border-slate-100 p-6 md:p-8 bg-white" data-testid="contact-form">
          <div className="grid grid-cols-1 sm:grid-cols-2 gap-4">
            <div>
              <label className="text-xs uppercase tracking-widest font-bold text-slate-500 block mb-2">Name *</label>
              <input
                data-testid="contact-name-input"
                value={form.name}
                onChange={set("name")}
                className="w-full px-4 py-3 rounded-xl border border-slate-200 text-sm focus:outline-none focus:border-[#173a7a]"
                placeholder="Your name"
              />
            </div>
            <div>
              <label className="text-xs uppercase tracking-widest font-bold text-slate-500 block mb-2">Email *</label>
              <input
                data-testid="contact-email-input"
                type="email"
                value={form.email}
                onChange={set("email")}
                className="w-full px-4 py-3 rounded-xl border border-slate-200 text-sm focus:outline-none focus:border-[#173a7a]"
                placeholder="you@example.com"
              />
            </div>
          </div>
          <div className="mt-4">
            <label className="text-xs uppercase tracking-widest font-bold text-slate-500 block mb-2">Subject</label>
            <input
              data-testid="contact-subject-input"
              value={form.subject}
              onChange={set("subject")}
              className="w-full px-4 py-3 rounded-xl border border-slate-200 text-sm focus:outline-none focus:border-[#173a7a]"
              placeholder="What's this about?"
            />
          </div>
          <div className="mt-4">
            <label className="text-xs uppercase tracking-widest font-bold text-slate-500 block mb-2">Message *</label>
            <textarea
              data-testid="contact-message-input"
              value={form.message}
              onChange={set("message")}
              rows={6}
              className="w-full px-4 py-3 rounded-xl border border-slate-200 text-sm focus:outline-none focus:border-[#173a7a] resize-none"
              placeholder="Tell us how we can help…"
            />
          </div>
          <button
            data-testid="contact-submit-btn"
            disabled={loading}
            className="mt-6 inline-flex items-center gap-2 px-8 py-3.5 rounded-full bg-[#173a7a] text-white font-semibold hover:bg-[#1a4694] disabled:opacity-60"
          >
            {loading ? "Sending…" : <>Send Message <Send size={16} /></>}
          </button>
        </form>
      </section>

      <SiteFooter />
    </div>
  );
}
